// BotCatalog.jsx
// src/components/bot-management/BotCatalog.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { X, Bot, Plus, Trash2, Crown, Settings, Sparkles, ArrowRight, ChevronDown, CheckIcon, Star, Eye, EyeOff } from 'lucide-react';
import { HousePortal } from '@/App';
import { motion, AnimatePresence } from 'framer-motion';
import CustomBotModal from '@/components/layout/CustomBotModal';
import NextMessageOptionsModal from '@/components/tools/NextMessageOptionsModal'; 
import { botManager } from '@/managers/BotManager';
import { authManager } from '@/managers/AuthManager';
import { useConversation } from '@/managers/ConvoManager';
import PlatformTag from './PlatformTag'; 
import { DEFAULT_PROMPTED_BOT_ID, DEFAULT_PROMPTED_BOT_NAME } from '@/utils/defaultBotProfile';

/* ========================
 * useAuthSettings - Custom hook to access settings from AuthManager
 * Provides reactive access to settings stored in AuthManager
 * ======================== */
const useAuthSettings = () => {
  const [settings, setSettings] = useState(() => authManager.getSettings());

  useEffect(() => {
    const unsubscribe = authManager.subscribe((state) => {
      setSettings(state.settings);
    });
    return unsubscribe;
  }, []);

  return {
    settings,
    starModel: (botId, modelId, starred) => authManager.starModel(botId, modelId, starred),
    hideModel: (botId, modelId, hidden) => authManager.hideModel(botId, modelId, hidden),
    getModelPreferencesForBot: (botId) => authManager.getModelPreferencesForBot(botId)
  };
};

export const platformOrder = ['OPENAI', 'CLAUDE', 'GEMINI', 'MISTRAL', 'OPENROUTER', 'GROK'];

export const sortModelsWithPlatformPriority = (models = [], starredModelIds = [], availablePlatforms = {}) => {
    return [...models].sort((a, b) => {
        const platformA = String(a.platform || '').toUpperCase();
        const platformB = String(b.platform || '').toUpperCase();

        const aHasKey = !!availablePlatforms[platformA];
        const bHasKey = !!availablePlatforms[platformB];

        if (aHasKey && !bHasKey) return -1;
        if (!aHasKey && bHasKey) return 1;

        const aIsStarred = starredModelIds.includes(a.id);
        const bIsStarred = starredModelIds.includes(b.id);

        if (aIsStarred && !bIsStarred) return -1;
        if (!aIsStarred && bIsStarred) return 1;

        const platformAIndex = platformOrder.indexOf(platformA);
        const platformBIndex = platformOrder.indexOf(platformB);

        const effectiveAIndex = platformAIndex === -1 ? platformOrder.length : platformAIndex;
        const effectiveBIndex = platformBIndex === -1 ? platformOrder.length : platformBIndex;

        if (effectiveAIndex !== effectiveBIndex) {
            return effectiveAIndex - effectiveBIndex;
        }
        if (platformAIndex === -1 && platformBIndex === -1 && platformA !== platformB) {
            if (platformA < platformB) return -1;
            if (platformA > platformB) return 1;
        }

        const orderA = a.order !== undefined ? a.order : Infinity;
        const orderB = b.order !== undefined ? b.order : Infinity;
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        
        const nameA = a.name || a.cleanModelName || a.model || '';
        const nameB = b.name || b.cleanModelName || b.model || '';
        return nameA.localeCompare(nameB);
    });
};

const useBotData = () => {
    const [botState, setBotState] = useState(() => botManager.getState());
    useEffect(() => {
        const unsubscribe = botManager.subscribe(setBotState);
        return () => unsubscribe();
    }, []);

    return {
        allBots: botState.allBots || [],
        activeBotConfig: botState.activeBotConfig,
        availablePlatformsMap: botState.platformsWithActiveKeysMap || {}, 
        isLoading: !botState.isInitialized,
        isInitialized: botState.isInitialized,
        allPlatformModels: botState.allPlatformModels || [],
    };
};

const ModelOption = ({ 
    model, 
    isActive, 
    hasKey, 
    onSelect, 
    onNewChat, 
    botId,
    isStarred = false,
    isHidden = false,
    onToggleStar,
    onToggleHide,
    showControls = true 
}) => {
    const { starModel, hideModel } = useAuthSettings();

    const handleToggleStar = (e) => {
        e.stopPropagation();
        if (onToggleStar) {
             onToggleStar(model.id, !isStarred); 
        } else {
            starModel(botId, model.id, !isStarred); 
        }
    };

    const handleToggleHide = (e) => {
        e.stopPropagation();
        if (onToggleHide) {
            onToggleHide(model.id, !isHidden); 
        } else {
            hideModel(botId, model.id, !isHidden); 
        }
    };
    
    const modelName = model.name || model.cleanModelName || model.model || 'Unknown Model';

    return (
        <div 
            className={`
                px-2 py-1.5 rounded transition-colors duration-150 flex items-center justify-between group
                ${isActive ? 'bg-surface-dark' : 'hover:bg-surface-dark/60 cursor-pointer'}
                ${!hasKey ? 'opacity-50 cursor-not-allowed' : ''}
                ${isHidden ? 'opacity-60' : ''}
            `}
            onClick={hasKey ? () => onSelect(model) : undefined} 
        >
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <PlatformTag platform={model.platform} hasKey={hasKey} />
                <div className="flex items-center gap-1 flex-1 min-w-0">
                    {isStarred && (
                        <Star size={8} className="fill-yellow-400 text-yellow-400 flex-shrink-0" />
                    )}
                    <span className="text-xs text-on-surface-dim truncate">
                        {modelName}
                    </span>
                </div>
            </div>
            
            <div className="flex items-center gap-1">
                {showControls && hasKey && (
                    <div className="flex items-center gap-0.5">
                        <button
                            onClick={handleToggleStar}
                            className="p-0.5 rounded hover:bg-surface-dark transition-colors"
                            title={isStarred ? "Unstar model" : "Star model"}
                        >
                            <Star 
                                size={10} 
                                className={`transition-colors duration-200 ${
                                    isStarred 
                                        ? 'fill-yellow-400 text-yellow-400' 
                                        : 'text-on-surface-dim hover:text-yellow-400'
                                }`} 
                            />
                        </button>
                        <button
                            onClick={handleToggleHide}
                            className="p-0.5 rounded hover:bg-surface-dark transition-colors"
                            title={isHidden ? "Show model in sidebar" : "Hide model from sidebar"}
                        >
                            {isHidden ? (
                                <Eye size={10} className="text-on-surface-dim hover:text-primary" />
                            ) : (
                                <EyeOff size={10} className="text-on-surface-dim hover:text-error" />
                            )}
                        </button>
                    </div>
                )}
                
                {isActive && (
                    <div className="flex items-center gap-2 ml-2">
                        <button 
                            className="text-xs text-primary hover:underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                onNewChat(); 
                            }}
                        >
                            New Chat
                        </button>
                        <CheckIcon size={14} className="text-primary" /> 
                    </div>
                )}
            </div>
        </div>
    );
};

const ShelbyOption = ({ bot, isActive, availablePlatformsMap, onSelectModel, onNewChat, currentConversation }) => {
    const [showSettingsPanel, setShowSettingsPanel] = useState(false); 
    const [showHiddenModelsToggle, setShowHiddenModelsToggle] = useState(false);
    const { settings, getModelPreferencesForBot } = useAuthSettings();
    
    const allShelbyModels = useMemo(() => botManager.getAvailableModelsForBot(bot.id) || [], [bot.id, settings, botManager.getState().allPlatformModels, botManager.getState().platformModelsWithKeys]); 
    
    const { starredModels = [], hiddenModels = [] } = useMemo(() => {
        return getModelPreferencesForBot(bot.id);
    }, [getModelPreferencesForBot, bot.id, settings.zPreferences?.botModelPreferences?.modelPreferences]);

    const visibleModels = useMemo(() => {
        const filtered = allShelbyModels.filter(model => !hiddenModels.includes(model.id));
        return sortModelsWithPlatformPriority(filtered, starredModels, availablePlatformsMap);
    }, [allShelbyModels, hiddenModels, starredModels, availablePlatformsMap]);

    const hiddenModelsList = useMemo(() => {
        const filtered = allShelbyModels.filter(model => hiddenModels.includes(model.id));
        return sortModelsWithPlatformPriority(filtered, starredModels, availablePlatformsMap);
    }, [allShelbyModels, hiddenModels, starredModels, availablePlatformsMap]);

    // Truncate bot name to 18 characters
    const truncatedBotName = bot.name && bot.name.length > 18 
        ? `${bot.name.substring(0, 18)}...` 
        : bot.name;
        
    return (
        <>
            <div className="border-2 rounded-lg overflow-hidden transition-all duration-200 border-primary bg-primary/5 h-full flex flex-col">
                <div className="p-3 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-on-surface" title={bot.name}>
                            {truncatedBotName}
                        </span>
                    </div>
                     <button
                        onClick={(e) => { e.stopPropagation(); setShowSettingsPanel(true); }}
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium text-on-surface-dim hover:text-primary hover:bg-surface-dark transition-colors"
                        title="Open Tools & MCP Settings"
                    >
                        <span>Tools & MCP</span> 
                        <Settings size={14} />   
                    </button>
                </div>
                
                <div className="border-t border-[var(--border-color)] flex-1 flex flex-col min-h-0">
                    <div className="p-2 flex-shrink-0">
                        <p className="text-xs text-on-surface-dim">{bot.description}</p>
                        <p className="text-[10px] text-on-surface-dim mt-1 italic">✨ Star favorites • Hide unused models</p>
                    </div>
                    
                    <div className="overflow-y-auto custom-scrollbar flex-1 px-2 py-0 min-h-0">
                        <div className="space-y-1 pb-2">
                            {visibleModels.map(model => (
                                <ModelOption
                                    key={model.id}
                                    model={model}
                                    isActive={isActive && currentConversation?.modelId === model.id}
                                    hasKey={!!availablePlatformsMap[String(model.platform || '').toUpperCase()]}
                                    onSelect={() => onSelectModel(bot, model)}
                                    onNewChat={() => onNewChat(bot, model)}
                                    botId={bot.id}
                                    isStarred={starredModels.includes(model.id)}
                                    isHidden={false} 
                                    showControls={true}
                                />
                            ))}
                            
                            {hiddenModelsList.length > 0 && (
                                <>
                                    <div className="border-t border-[var(--border-color)] mt-2 pt-2">
                                        <button
                                            onClick={() => setShowHiddenModelsToggle(!showHiddenModelsToggle)}
                                            className="flex items-center gap-2 w-full px-1 py-1 text-xs text-on-surface-dim hover:text-on-surface transition-colors"
                                        >
                                            <ChevronDown size={12} className={`transition-transform ${showHiddenModelsToggle ? 'rotate-180' : ''}`} />
                                            Hidden Models ({hiddenModelsList.length})
                                        </button>
                                    </div>
                                    {showHiddenModelsToggle && (
                                        <div className="space-y-1 mt-1">
                                            {hiddenModelsList.map(model => (
                                                <ModelOption
                                                    key={model.id}
                                                    model={model}
                                                    isActive={false} 
                                                    hasKey={!!availablePlatformsMap[String(model.platform || '').toUpperCase()]}
                                                    onSelect={() => onSelectModel(bot, model)} 
                                                    onNewChat={() => onNewChat(bot, model)}
                                                    botId={bot.id}
                                                    isStarred={starredModels.includes(model.id)}
                                                    isHidden={true} 
                                                    showControls={true}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {showSettingsPanel && (
                    <HousePortal> 
                        <NextMessageOptionsModal isOpen={showSettingsPanel} onClose={() => setShowSettingsPanel(false)} />
                    </HousePortal>
                )}
            </AnimatePresence>
        </>
    );
};

const CustomBotRowOption = ({ 
    bot, 
    isActive, 
    availablePlatformsMap, 
    onSelectModel,
    onNewChat,
    onEdit, 
    onDelete,
    currentConversation
}) => {
    const [expanded, setExpanded] = useState(isActive); 
    const [showHiddenModelsToggle, setShowHiddenModelsToggle] = useState(false);
    const { settings, getModelPreferencesForBot } = useAuthSettings();
    
    const allBotModels = useMemo(() => botManager.getAvailableModelsForBot(bot.id) || [], [bot.id, settings, botManager.getState().allPlatformModels, botManager.getState().platformModelsWithKeys]);
    
    const { starredModels = [], hiddenModels = [] } = useMemo(() => {
        return getModelPreferencesForBot(bot.id);
    }, [getModelPreferencesForBot, bot.id, settings.zPreferences?.botModelPreferences?.modelPreferences]);

    const visibleModels = useMemo(() => {
        const filtered = allBotModels.filter(model => !hiddenModels.includes(model.id));
        return sortModelsWithPlatformPriority(filtered, starredModels, availablePlatformsMap);
    }, [allBotModels, hiddenModels, starredModels, availablePlatformsMap]);

    const hiddenModelsList = useMemo(() => {
        const filtered = allBotModels.filter(model => hiddenModels.includes(model.id));
        return sortModelsWithPlatformPriority(filtered, starredModels, availablePlatformsMap);
    }, [allBotModels, hiddenModels, starredModels, availablePlatformsMap]);
        
    const isVanillaBot = bot.id === 'vanillabot'; 
    const isTrulyCustomBot = bot.isCustomBot && !isVanillaBot; 

    useEffect(() => { 
      setExpanded(isActive);
    }, [isActive]);

    // Truncate bot name to 18 characters
    const truncatedBotName = bot.name && bot.name.length > 18 
        ? `${bot.name.substring(0, 18)}...` 
        : bot.name;

    return (
        <div className={`
            rounded-lg overflow-hidden border transition-all duration-200
            ${isActive ? 'border-primary bg-primary/5' : 'border-[var(--border-color)] bg-surface'}
            ${isVanillaBot ? 'border-dashed border-primary/40 bg-primary/5' : ''}
        `}>
            <div 
                className="p-3 cursor-pointer flex items-center justify-between"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <Bot className={`w-4 h-4 ${isActive || isVanillaBot ? 'text-primary' : 'text-on-surface-dim'}`} />
                        <span className={`text-sm font-medium ${isVanillaBot ? 'text-primary' : 'text-on-surface'}`} title={bot.name}>
                            {truncatedBotName}
                            {isVanillaBot && <span className="text-xs text-primary/70 ml-1">(Universal Base)</span>}
                        </span>
                    </div>
                    {bot.description && !isVanillaBot && ( 
                        <span className="text-xs text-on-surface-dim truncate flex-1 ml-2">
                            {bot.description}
                        </span>
                    )}
                </div>
                
                <div className="flex items-center gap-1.5">
                    {isTrulyCustomBot && onEdit && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(bot.id); }}
                            className="p-1 rounded-md text-on-surface-dim hover:text-primary hover:bg-surface-dark transition"
                            title="Edit Custom Bot"
                        > <Settings size={12} /> </button>
                    )}
                    {isTrulyCustomBot && onDelete && (
                         <button
                            onClick={(e) => { e.stopPropagation(); onDelete(bot, e); }}
                            className="p-1 rounded-md text-on-surface-dim hover:text-error hover:bg-surface-dark transition"
                            title="Delete Custom Bot"
                        > <Trash2 size={12} /> </button>
                    )}
                    <ChevronDown className={`w-4 h-4 text-on-surface-dim transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </div>
            </div>
            
            {expanded && (
                <div className="border-t border-[var(--border-color)]">
                    <div className="p-3 max-h-64 overflow-y-auto custom-scrollbar">
                        {isVanillaBot && (
                             <p className="text-xs text-on-surface-dim mb-2 px-1">
                                {bot.description || 'Directly use any available model without a specific persona.'}
                            </p>
                        )}
                        <div className="space-y-1">
                            {visibleModels.map(model => (
                                <ModelOption
                                    key={model.id}
                                    model={model}
                                    isActive={isActive && currentConversation?.modelId === model.id}
                                    hasKey={!!availablePlatformsMap[String(model.platform || '').toUpperCase()]}
                                    onSelect={() => onSelectModel(bot, model)}
                                    onNewChat={() => onNewChat(bot, model)}
                                    botId={bot.id}
                                    isStarred={starredModels.includes(model.id)}
                                    isHidden={false}
                                    showControls={true}
                                />
                            ))}
                            {hiddenModelsList.length > 0 && (
                                <>
                                    <div className="border-t border-[var(--border-color)] mt-2 pt-2">
                                        <button
                                            onClick={() => setShowHiddenModelsToggle(!showHiddenModelsToggle)}
                                            className="flex items-center gap-2 w-full px-1 py-1 text-xs text-on-surface-dim hover:text-on-surface transition-colors"
                                        >
                                            <ChevronDown size={12} className={`transition-transform ${showHiddenModelsToggle ? 'rotate-180' : ''}`} />
                                            Hidden Models ({hiddenModelsList.length})
                                        </button>
                                    </div>
                                    {showHiddenModelsToggle && (
                                        <div className="space-y-1 mt-1">
                                            {hiddenModelsList.map(model => (
                                                <ModelOption
                                                    key={model.id}
                                                    model={model}
                                                    isActive={false}
                                                    hasKey={!!availablePlatformsMap[String(model.platform || '').toUpperCase()]}
                                                    onSelect={() => onSelectModel(bot, model)}
                                                    onNewChat={() => onNewChat(bot, model)}
                                                    botId={bot.id}
                                                    isStarred={starredModels.includes(model.id)}
                                                    isHidden={true}
                                                    showControls={true}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                             {visibleModels.length === 0 && hiddenModelsList.length === 0 && (
                                <p className="text-xs text-on-surface-dim text-center py-2">No models available for this bot.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const BotCatalog = ({ onClose, initialEditingBotId = null }) => {
    const [showCustomBotModal, setShowCustomBotModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [botToDelete, setBotToDelete] = useState(null);
    const [editingBotId, setEditingBotId] = useState(null); 
    
    const { allBots, isLoading, availablePlatformsMap, activeBotConfig } = useBotData();
    const { settings } = useAuthSettings(); 
    const { tier: userTier, user } = authManager.getState(); 
    const userId = user?.uid;
    const { createConversation, currentConversation } = useConversation();

    useEffect(() => { 
        setEditingBotId(initialEditingBotId);
        if (initialEditingBotId) { 
            setShowCustomBotModal(true);
        }
    }, [initialEditingBotId]);


    const handleBotAndModelSelection = async (selectedBot, selectedModel) => {
        try {
            if (activeBotConfig?.botId === selectedBot.id && activeBotConfig?.modelId === selectedModel.id) {
                onClose?.();
                return;
            }
            botManager.setCurrentBotAndModel(selectedBot.id, selectedModel.id); 
            const result = await createConversation({ 
                botId: selectedBot.id, 
                modelId: selectedModel.id, 
                customSystemMessage: selectedBot.customSystemMessage || null 
            });

            if (result.success) {
                onClose?.();
            } else {
                console.error('Failed to create conversation:', result.error);
            }
        } catch (error) {
            console.error('Error in bot selection:', error);
        }
    };

    const handleNewChatRequest = async (selectedBot, selectedModel) => {
        try {
            const result = await createConversation({ 
                botId: selectedBot.id, 
                modelId: selectedModel.id, 
                customSystemMessage: selectedBot.customSystemMessage || null, 
                forceNew: true 
            });
            if (result.success) {
                onClose?.();
            } else {
                console.error('Failed to create new conversation:', result.error);
            }
        } catch (error) {
            console.error('Error creating new chat:', error);
        }
    };

    const handleDeleteRequest = (bot, e) => {
        e.stopPropagation();
        setBotToDelete(bot);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteAction = async () => {
        if (!botToDelete?.id || !userId) return;
        if (!botToDelete.id.startsWith(`custom_${userId}_`)) {
             console.error('Unauthorized deletion attempt or not a custom bot:', botToDelete.id);
             setShowDeleteConfirm(false);
             setBotToDelete(null);
             return;
        }
        try {
            await botManager.deleteCustomBot(botToDelete.id); 
            setShowDeleteConfirm(false);
            setBotToDelete(null);
        } catch (error) {
            console.error('Failed to delete bot:', error);
        }
    };

    const handleEditRequest = (botIdToEdit) => {
        setEditingBotId(botIdToEdit);
        setShowCustomBotModal(true);
    };
    
    const proLevelTiers = ['Pro', 'Beta', 'Pro Secure', 'Admin']; 
    const hasProAccess = proLevelTiers.includes(userTier);

    const defaultPromptedBot = useMemo(() => allBots.find(b => b.id === DEFAULT_PROMPTED_BOT_ID), [allBots]);
    const vanillaBot = useMemo(() => allBots.find(b => b.id === 'vanillabot'), [allBots]); 
    
    const userCustomBots = useMemo(() => {
        if (!userId || !allBots) return []; 
        return allBots.filter(b => b.isCustomBot && b.id.startsWith(`custom_${userId}_`));
    }, [allBots, userId]);


    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    if (isLoading) {
        return (
            <HousePortal>
                <motion.div className="fixed inset-0 z-[5000] flex items-center justify-center bg-background/80 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                        <Bot className="w-5 h-5 text-primary animate-bounce" />
                        <span className="text-on-surface text-base">Loading Catalog...</span>
                    </div>
                </motion.div>
            </HousePortal>
        );
    }

    return (
        <HousePortal>
            <motion.div className="fixed inset-0 z-[5000]">
                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
                <motion.div 
                    className="absolute top-[10%] bottom-[10%] left-[8%] right-[8%] lg:left-[10%] lg:right-[10%] xl:left-[12%] xl:right-[12%]
                        flex flex-col rounded-xl bg-surface border border-[var(--border-color)] 
                        shadow-xl overflow-hidden max-w-[1000px] mx-auto"
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
                        <div className="flex items-center gap-2">
                            <Bot className="w-4 h-4 text-primary" />
                            <h2 className="text-base font-medium text-on-surface">Choose Your Profile</h2>
                        </div>
                        <button onClick={onClose} className="p-1 hover:bg-surface-dark rounded-lg transition-all">
                            <X className="w-4 h-4 text-on-surface" />
                        </button>
                    </div>

                    <div className="flex-1 p-4 overflow-hidden min-h-0">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
                            <div className="lg:col-span-1 flex flex-col min-h-0">
                                <h3 className="text-xs font-medium text-on-surface-dim mb-2 flex-shrink-0">Default Prompted Profile</h3>
                                {defaultPromptedBot && (
                                    <div className="flex-1 min-h-0">
                                        <ShelbyOption 
                                            bot={defaultPromptedBot}
                                            isActive={currentConversation?.botId === defaultPromptedBot.id}
                                            availablePlatformsMap={availablePlatformsMap}
                                            onSelectModel={handleBotAndModelSelection}
                                            onNewChat={handleNewChatRequest}
                                            currentConversation={currentConversation}
                                        />
                                    </div>
                                )}
                            </div>
                            
                            <div className="lg:col-span-2 flex flex-col min-h-0">
                                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                    <h3 className="text-xs font-medium text-on-surface-dim">Custom Profiles & Raw Mode</h3>
                                    {hasProAccess && (
                                        <button
                                            onClick={() => { setEditingBotId(null); setShowCustomBotModal(true);}}
                                            className="flex items-center gap-1 px-2 py-1 bg-primary/10 hover:bg-primary/20 text-primary text-xs rounded-md transition-colors"
                                        > <Plus className="w-3 h-3" /> Create Custom </button>
                                    )}
                                </div>
                                
                                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                                    <div className="space-y-2">
                                        {vanillaBot && ( 
                                            <CustomBotRowOption 
                                                bot={vanillaBot} 
                                                isActive={currentConversation?.botId === vanillaBot.id}
                                                availablePlatformsMap={availablePlatformsMap}
                                                onSelectModel={handleBotAndModelSelection}
                                                onNewChat={handleNewChatRequest}
                                                currentConversation={currentConversation}
                                            />
                                        )}
                                        
                                        {userCustomBots.map(customBot => (
                                            <CustomBotRowOption 
                                                key={customBot.id}
                                                bot={customBot}
                                                isActive={currentConversation?.botId === customBot.id}
                                                availablePlatformsMap={availablePlatformsMap}
                                                onSelectModel={handleBotAndModelSelection}
                                                onNewChat={handleNewChatRequest}
                                                onEdit={handleEditRequest}
                                                onDelete={handleDeleteRequest}
                                                currentConversation={currentConversation}
                                            />
                                        ))}
                                    </div>
                                </div>
                                
                                {!hasProAccess && (
                                    <div className="mt-3 p-3 border border-dashed border-[var(--border-color)] rounded-lg bg-surface-dark/30 flex items-center justify-between flex-shrink-0">
                                        <div className="flex items-center gap-2">
                                            <Crown className="w-4 h-4 text-yellow-500/80" />
                                            <span className="text-xs text-on-surface-dim">Create custom assistants with Pro subscription</span>
                                        </div>
                                        <button className="px-2 py-1 text-xs text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors">
                                            Upgrade
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>

                <AnimatePresence>
                    {showDeleteConfirm && (
                        <motion.div 
                            className="fixed inset-0 z-[5200] flex items-center justify-center"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        >
                            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowDeleteConfirm(false); setBotToDelete(null); }} />
                            <motion.div 
                                className="relative bg-surface p-4 rounded-lg max-w-sm w-full mx-4"
                                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            >
                                <h3 className="text-base font-medium text-on-surface mb-2">Delete Custom Assistant</h3>
                                <p className="text-sm text-on-surface-dim mb-4">
                                    Are you sure you want to delete "{botToDelete?.name}"? This cannot be undone.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={confirmDeleteAction}
                                        className="flex-1 px-3 py-1.5 bg-error hover:bg-error/90 text-white text-sm rounded-md transition-colors"
                                    > Delete </button>
                                    <button
                                        onClick={() => { setShowDeleteConfirm(false); setBotToDelete(null); }}
                                        className="flex-1 px-3 py-1.5 border border-border-color text-on-surface-dim hover:bg-surface-dark text-sm rounded-md transition-colors"
                                    > Cancel </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            <CustomBotModal
                isOpen={showCustomBotModal}
                onClose={() => { setShowCustomBotModal(false); setEditingBotId(null); }} 
                botId={editingBotId} 
                onBotUpdated={() => botManager.refreshBots()} 
            />
        </HousePortal>
    );
};

export default BotCatalog;